'use client';

import { useState } from 'react';
import { AppIcon } from '@impacta/shared';

export interface FilterState {
  categories: string[];
  priceRange: [number, number];
  levels: string[];
  locations: string[];
  durationRanges: string[];
  dateFrom: string;
  dateTo: string;
}

export interface FilterOption {
  value: string;
  label: string;
  disabled?: boolean;
  checked?: boolean;
}

export interface DurationBucket {
  key: string;
  label: string;
  min: number;
  max: number | null;
}

interface FilterSidebarProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onClear: () => void;
  activeCount: number;
  categoryOptions: FilterOption[];
  maxPrice: number;
  levelOptions: FilterOption[];
  locationOptions: FilterOption[];
  durationBuckets: FilterOption[];
  minDate?: string;
  maxDate?: string;
}

function FilterGroup({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: FilterOption[];
  selected: string[];
  onToggle: (val: string) => void;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-[#E8E8E4] py-4">
      <button
        className="flex items-center justify-between w-full text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-sm font-bold text-[#373A36]">{title}</span>
        <AppIcon
          name={open ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={16}
          className="text-[#6b6e68]"
        />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {options.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-2.5 group ${
                opt.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={opt.checked ?? selected.includes(opt.value)}
                onChange={() => !opt.disabled && onToggle(opt.value)}
                disabled={opt.disabled}
                className="w-4 h-4 rounded border-[#E8E8E4] accent-[#00A19B] cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
              />
              <span
                className={`text-sm transition-colors ${
                  opt.disabled ? 'text-[#9ca3a0]' : 'text-[#6b6e68] group-hover:text-[#373A36]'
                }`}
              >
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DateFilterGroup({
  title,
  from,
  to,
  minDate,
  maxDate,
  onChange,
}: {
  title: string;
  from: string;
  to: string;
  minDate?: string;
  maxDate?: string;
  onChange: (from: string, to: string) => void;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-[#E8E8E4] py-4">
      <button
        className="flex items-center justify-between w-full text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-sm font-bold text-[#373A36]">{title}</span>
        <AppIcon
          name={open ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={16}
          className="text-[#6b6e68]"
        />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-xs text-[#6b6e68] mb-1.5">Desde</label>
            <input
              type="date"
              lang="es"
              value={from}
              min={minDate}
              max={to || maxDate}
              onChange={(e) => onChange(e.target.value, to)}
              className="w-full px-3 py-2.5 rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] text-sm text-[#373A36] focus:outline-none focus:border-[#00A19B]"
            />
          </div>
          <div>
            <label className="block text-xs text-[#6b6e68] mb-1.5">Hasta</label>
            <input
              type="date"
              lang="es"
              value={to}
              min={from || minDate}
              max={maxDate}
              onChange={(e) => onChange(from, e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] text-sm text-[#373A36] focus:outline-none focus:border-[#00A19B]"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function FilterSidebar({
  filters,
  onChange,
  onClear,
  activeCount,
  categoryOptions,
  maxPrice,
  levelOptions,
  locationOptions,
  durationBuckets,
  minDate,
  maxDate,
}: FilterSidebarProps) {
  const toggleArray = (key: keyof FilterState, val: string) => {
    const current = filters[key] as string[];
    const updated = current.includes(val) ? current.filter((v) => v !== val) : [...current, val];
    onChange({ ...filters, [key]: updated });
  };

  const toggleCategory = (val: string) => toggleArray('categories', val);
  const toggleLevel = (val: string) => toggleArray('levels', val);
  const toggleLocation = (val: string) => toggleArray('locations', val);
  const toggleDuration = (val: string) => toggleArray('durationRanges', val);

  const handleDateChange = (dateFrom: string, dateTo: string) => {
    onChange({ ...filters, dateFrom, dateTo });
  };

  const tipoOptions: FilterOption[] = [
    { value: 'presencial', label: 'Presencial', checked: true, disabled: true },
    { value: 'online', label: 'Online', disabled: true },
  ];

  return (
    <aside className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <AppIcon name="FunnelIcon" size={18} className="text-[#373A36]" />
          <h2 className="text-base font-bold text-[#373A36]">Filtros</h2>
          {activeCount > 0 && (
            <span
              className="w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center text-white"
              style={{ backgroundColor: '#00A19B' }}
            >
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            onClick={onClear}
            className="text-xs font-semibold text-[#00A19B] hover:underline"
          >
            Limpiar
          </button>
        )}
      </div>

      {/* Tipo */}
      <FilterGroup title="Tipo" options={tipoOptions} selected={[]} onToggle={() => {}} />

      {/* Fecha */}
      <DateFilterGroup
        title="Fecha"
        from={filters.dateFrom}
        to={filters.dateTo}
        minDate={minDate}
        maxDate={maxDate}
        onChange={handleDateChange}
      />

      {/* Ubicación */}
      <FilterGroup
        title="Ubicación"
        options={locationOptions}
        selected={filters.locations}
        onToggle={toggleLocation}
      />

      {/* Nivel */}
      <FilterGroup
        title="Nivel"
        options={levelOptions}
        selected={filters.levels}
        onToggle={toggleLevel}
      />

      {/* Duracion */}
      <FilterGroup
        title="Duracion"
        options={durationBuckets}
        selected={filters.durationRanges}
        onToggle={toggleDuration}
      />

      {/* Price Range */}
      <div className="border-b border-[#E8E8E4] py-4">
        <label htmlFor="price-range" className="block text-sm font-bold text-[#373A36] mb-3">
          Precio maximo
        </label>
        <div className="space-y-2">
          <input
            id="price-range"
            type="range"
            min={0}
            max={maxPrice}
            step={5000}
            value={filters.priceRange[1]}
            onChange={(e) =>
              onChange({
                ...filters,
                priceRange: [0, parseInt(e.target.value)],
              })
            }
            className="w-full accent-[#00A19B]"
            aria-label="Rango de precios"
          />
          <div className="flex justify-between text-xs text-[#6b6e68]">
            <span>$0</span>
            <span className="font-semibold text-[#373A36]">
              ${filters.priceRange[1].toLocaleString('es-CL')}
            </span>
          </div>
        </div>
      </div>

      <FilterGroup
        title="Categoria"
        options={categoryOptions}
        selected={filters.categories}
        onToggle={toggleCategory}
      />
    </aside>
  );
}
